import React, { useRef, useEffect } from "react";
import { BaseEditor, Editor, Range } from "slate";
import { useSlate, useFocused } from "slate-react";
import { Button } from "../../common/Button";
import { Icon } from "../../common/Icon";
import { MaterialSymbol } from "@material-symbols/font-400";

/**
 * Hovering Toolbar component.
 */
export const HoveringToolbar: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const editor = useSlate();
  const isFocused = useFocused();

  useEffect(() => {
    const el = ref.current;
    const { selection } = editor;

    if (!el) {
      return;
    }

    if (
      !selection ||
      !isFocused ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ""
    ) {
      el.style.opacity = "0";
      el.style.top = "-10000px";
      el.style.left = "-10000px";
      return;
    }

    const domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount === 0) {
      return;
    }

    const domRange = domSelection.getRangeAt(0);
    const rect = domRange.getBoundingClientRect();

    el.style.opacity = "1";
    el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight - 5}px`;
    el.style.left = `${
      Math.max(rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2,0)
    }px`;
  });

  /**
   * Keyboard shortcut handler for formatting marks.
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
      if (event.key === "b") {
        event.preventDefault();
        toggleMark(editor, "bold");
      } else if (event.key === "i") {
        event.preventDefault();
        toggleMark(editor, "italic");
      } else if (event.key === "u") {
        event.preventDefault();
        toggleMark(editor, "underline");
      } else if (event.key === "s") {
        event.preventDefault();
        toggleMark(editor, "strikethrough");
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor]);

  return (
    <div
      ref={ref}
      className="fixed z-10 top-0 left-0 bg-gray-800 text-white rounded-md p-2 shadow-lg transition-opacity duration-300 flex gap-2 bg-gray-1"
      style={{
        opacity: 0,
      }}
      onMouseDown={(e) => {
        // Prevent toolbar from taking focus away from the editor
        e.preventDefault();
      }}
    >
      <FormatButton format="bold" icon="format_bold" />
      <FormatButton format="italic" icon="format_italic" />
      <FormatButton format="underline" icon="format_underlined" />
      <FormatButton format="strikethrough" icon="format_strikethrough" />
    </div>
  );
};

/**
 * Button for applying text formatting (bold, italic, underline, strikethrough).
 */
const FormatButton: React.FC<{ format: string; icon: string }> = ({
                                                                    format,
                                                                    icon,
                                                                  }) => {
  const editor = useSlate();

  return (
    <Button
      onClick={() => toggleMark(editor, format)}
      icon={"only"}
      size={"xs"}
    >
      <Icon name={icon as MaterialSymbol} />
    </Button>
  );
};

/**
 * Toggles a specific mark (e.g., bold, italic, underline, strikethrough) on the selected text.
 */
const toggleMark = (editor: BaseEditor, format: string) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

/**
 * Checks if a specific mark (e.g., bold, italic, underline, strikethrough) is active on the selected text.
 */
const isMarkActive = (editor: BaseEditor, format: string): boolean => {
  const marks = Editor.marks(editor) as Record<string, boolean>;
  return marks ? marks[format] === true : false;
};
